import React from 'react';
import { Zap, Target, Shield, Lightbulb, BrainCircuit, FileCode } from 'lucide-react';
import { motion } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useCountUp from '../hooks/useCountUp';
import './WhyChooseUs.css';

const whyData = [
  {
    icon: BrainCircuit,
    title: 'AI-Native Approach',
    description: 'We build with AI at the core—not as an afterthought. Every solution is designed AI-first, embedding intelligent capabilities from day one for maximum impact.'
  },
  {
    icon: FileCode,
    title: 'Spec-Driven Development',
    description: 'AI-powered spec-driven development ensures precision from concept to code. We use AI to generate, validate, and iterate on specifications for faster, error-free delivery.'
  },
  {
    icon: Zap,
    title: 'Cutting-Edge Technology',
    description: 'We leverage the latest AI, ML, and IoT technologies to build future-ready solutions that give you a competitive advantage.'
  },
  {
    icon: Target,
    title: 'Industry Expertise',
    description: 'Deep domain knowledge across healthcare, telecommunications, retail, and smart city solutions with proven track records.'
  },
  {
    icon: Shield,
    title: 'Reliable & Scalable',
    description: 'Enterprise-grade solutions built for reliability, security, and seamless scalability as your business grows.'
  },
  {
    icon: Lightbulb,
    title: 'Innovation-First Approach',
    description: 'We don\'t just build software—we solve complex business challenges with creative, innovative technology solutions.'
  }
];

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.12, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const WhyChooseUs = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: bannerRef, isVisible: bannerVisible } = useScrollAnimation({ threshold: 0.3 });

  const satisfaction = useCountUp('98%', bannerVisible);
  const delivery = useCountUp('100%', bannerVisible);

  return (
    <section id="why-choose-us" className="why-section">
      <div className="why-background-overlay" />
      <div className="why-container">
        <motion.div
          ref={headerRef}
          className="why-header"
          variants={sectionVariants}
          initial="hidden"
          animate={headerVisible ? 'visible' : 'hidden'}
        >
          <div className="section-label">Why Us</div>
          <h2 className="display-large">Why Choose <span className="gradient-text">VYZN Info Tech</span></h2>
          <p className="body-medium">We combine technical excellence with deep industry knowledge to deliver transformative solutions</p>
        </motion.div>

        <div ref={gridRef} className="why-grid">
          {whyData.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                className="why-card dark-hover"
                custom={index}
                variants={cardVariants}
                initial="hidden"
                animate={gridVisible ? 'visible' : 'hidden'}
                whileHover={{ y: -8, boxShadow: '0 12px 40px rgba(0, 255, 209, 0.2)' }}
                transition={{ duration: 0.25 }}
              >
                <motion.div
                  className="why-icon"
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                >
                  <Icon size={32} />
                </motion.div>
                <h3 className="heading-2">{item.title}</h3>
                <p className="body-small">{item.description}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          ref={bannerRef}
          className="why-stats-banner"
          variants={sectionVariants}
          initial="hidden"
          animate={bannerVisible ? 'visible' : 'hidden'}
        >
          {[
            { value: satisfaction, label: 'Client Satisfaction' },
            { value: '24/7', label: 'Technical Support' },
            { value: delivery, label: 'On-Time Delivery' },
          ].map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="stat-divider-vertical" />}
              <motion.div
                className="stat-highlight"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={bannerVisible ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.2 + i * 0.15, duration: 0.5 }}
              >
                <div className="stat-highlight-number">{stat.value}</div>
                <div className="stat-highlight-label">{stat.label}</div>
              </motion.div>
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;